import { FC, useContext, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from 'components/ui/Button/Button';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { showError, showSuccess } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import useRecaptcha from 'hooks/useRecaptcha';
import { htmlToBase64 } from 'utils/shared/utils';
import { getProposalPdfByUrl } from 'services/proposal';
import LoaderMain from '../LoaderMain';
import { sendEmailProposal } from 'services/cms';
import withErrorBoundary from '../withErrorBoundary';
import { faDownload, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import md5 from 'md5';
import { MainContext } from 'contexts/MainContext';
import { useProposalUi } from './Assistant/proposalActions';

interface ProposalActionsProps {
  proposal: ProposalData | undefined;
  shareLinkOnly?: boolean;
  assistantOwner?: boolean;
}

const ProposalActions: FC<ProposalActionsProps> = ({ proposal, shareLinkOnly, assistantOwner = false }) => {
  const { t } = useTranslation();
  const [urlParams] = useSearchParams();
  const { user } = useContext(MainContext);
  const [email, setEmail] = useState(user?.email || '');
  const emailRef = useRef(email);
  emailRef.current = email;
  const [loading, setLoading] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [loadingShare, setLoadingShare] = useState<'whatsapp' | 'telegram' | null>(null);
  const { handleRecapcha } = useRecaptcha();

  const getProposalId = () => {
    const param = urlParams.get('id2');
    const key =
      proposal?.id + '_' + md5(proposal?.id + '') + '_' + proposal?.userC + '_' + proposal?.companyId;
    return param || key;
  };

  const getTripBuilderShareUrl = (proposalId: string) =>
    `https://proposal.beprotravel.com/trip-builder/?id2=${encodeURIComponent(proposalId)}`;

  const getHotelNames = () =>
    (proposal?.listOfProducts || []).flatMap((product) =>
      product.listOfItems.flatMap((item) =>
        (item.hotels || []).map((hotel) => hotel.hotel?.name?.trim()).filter(Boolean),
      ),
    ) as string[];

  const getBody = () => {
    const bodyHtml = document.querySelector('#pr-body')?.innerHTML;
    let newBodyHtml = bodyHtml?.replaceAll(
      /https:\/\/[^/]+\/backend\/s3/g,
      'https://s3-eu-west-1.amazonaws.com/'
    );
    const headerHtml = document.querySelector('#pr-header')?.innerHTML;
    let newHeaderHtml = headerHtml
      ?.replaceAll(/https:\/\/[^/]+\/backend\/s3/g, 'https://s3-eu-west-1.amazonaws.com/')
      .replaceAll(/https:\/\/[^/]+\/backend\/nocache/g, 'https://s3-eu-west-1.amazonaws.com/');

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newHeaderHtml || '';
    const pencilElements = tempDiv.querySelectorAll('.fa-pencil, svg[data-icon="pencil"]');
    pencilElements.forEach((el) => el.remove());
    newHeaderHtml = tempDiv.innerHTML;

    tempDiv.innerHTML = newBodyHtml || '';
    tempDiv.querySelectorAll('span.pr-edit-head, .fa-pencil, svg[data-icon="pencil"]').forEach((el) => el.remove());
    newBodyHtml = tempDiv.innerHTML;
    const footerHtml = document.querySelector('#pr-footer')?.innerHTML;
    let newFooterHtml = footerHtml?.replaceAll(
      /https:\/\/[^/]+\/backend\/s3/g,
      'https://s3-eu-west-1.amazonaws.com/'
    );

    return {
      printHtml: htmlToBase64(newBodyHtml || ''),
      header: htmlToBase64(newHeaderHtml || ''),
      footer: htmlToBase64(newFooterHtml || ''),
    };
  };

  const onClickPrint = async () => {
    const proposalId = getProposalId();
    if (!proposalId) {
      return;
    }
    setLoadingPrint(true);

    const recaptchaToken = await handleRecapcha();

    if (!recaptchaToken) {
      setLoadingPrint(false);
      return;
    }

    const body = getBody();
    const res = await getProposalPdfByUrl({
      previewKeyPublic: proposalId,
      recaptchaToken: recaptchaToken,
      ...body,
    });

    if (res?.pdfUrl) {
      window.open(res.pdfUrl, '_blank');
    } else if (!res?.pdfUrl || res.hasErrors) {
      showError(t('something_went_wrong'));
    }
    setLoadingPrint(false);
  };

  const onClickSendEmail = async () => {
    const proposalId = getProposalId();
    if (!proposalId || !emailRef.current) {
      return;
    }

    setLoading(true);

    const recaptchaToken = await handleRecapcha();

    if (!recaptchaToken) {
      setLoading(false);
      return;
    }
    const body = getBody();
    const res = await getProposalPdfByUrl({
      previewKeyPublic: proposalId,
      recaptchaToken: recaptchaToken,
      ...body,
    });

    if (!res.pdfUrl) {
      setLoading(false);
      return;
    }
    const proposalUrl = getTripBuilderShareUrl(proposalId);
    const response = await sendEmailProposal(emailRef.current, res.pdfUrl, proposalId, getHotelNames(), proposalUrl);

    if (response && !response?.hasErrors) {
      showSuccess(t('mo.email_success'));
    } else {
      showError(t('something_went_wrong'));
    }
    setLoading(false);
  };

  const getPdfUrl = async (): Promise<string | null> => {
    const proposalId = getProposalId();
    if (!proposalId) {
      return null;
    }

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) {
      return null;
    }

    const body = getBody();
    const res = await getProposalPdfByUrl({
      previewKeyPublic: proposalId,
      recaptchaToken: recaptchaToken,
      ...body,
    });

    if (!res.pdfUrl) {
      return null;
    }

    return res.pdfUrl.replace('s3.eu-west-1.amazonaws.com/prod.beprotravel', '25issta.beprotravel.com/backend/data/download');
  };

  const onClickShareWhatsApp = async () => {
    if (shareLinkOnly) {
      const proposalId = getProposalId();
      if (!proposalId) {
        return;
      }
      const shareUrl = getTripBuilderShareUrl(proposalId);
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`, '_blank');
      return;
    }

    setLoadingShare('whatsapp');
    const urlClear = await getPdfUrl();

    if (!urlClear) {
      setLoadingShare(null);
      return;
    }

    const message = t('proposal.whatsapp_message', { url: urlClear });
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setLoadingShare(null);
  };

  const onClickShareTelegram = async () => {
    if (shareLinkOnly) {
      const proposalId = getProposalId();
      if (!proposalId) {
        return;
      }
      const shareUrl = getTripBuilderShareUrl(proposalId);
      window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`, '_blank');
      return;
    }

    setLoadingShare('telegram');
    const urlClear = await getPdfUrl();

    if (!urlClear) {
      setLoadingShare(null);
      return;
    }

    const message = t('proposal.telegram_message', { url: urlClear });
    const url = `https://t.me/share/url?url=${encodeURIComponent(urlClear)}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setLoadingShare(null);
  };

  useProposalUi(
    (intent) => !!assistantOwner && intent.kind === 'share',
    (intent) => {
      if (intent.kind !== 'share') {
        return;
      }
      if (intent.email) {
        emailRef.current = intent.email;
        setEmail(intent.email);
      }
      if (intent.channel === 'email') {
        void onClickSendEmail();
      }
      if (intent.channel === 'pdf') {
        void onClickPrint();
      }
      if (intent.channel === 'whatsapp') {
        void onClickShareWhatsApp();
      }
      if (intent.channel === 'telegram') {
        void onClickShareTelegram();
      }
    },
  );

  return (
    <div className="pr-actions">
      <div className="pr-actions-bar">
        <div className="pr-actions-left">
          <input
            value={email}
            className="form-control pr-actions-email"
            placeholder="Email"
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button
            className="btn btn-primary pr-actions-btn"
            title=""
            icon={faEnvelope}
            onClick={onClickSendEmail}
            disabled={loading}
          />
          <Button
            className="btn btn-whatsapp pr-actions-btn"
            title=""
            icon={faWhatsapp}
            onClick={onClickShareWhatsApp}
            disabled={loadingShare === 'whatsapp'}
          />
          <Button
            className="btn btn-telegram pr-actions-btn"
            title=""
            icon={faTelegram}
            onClick={onClickShareTelegram}
            disabled={loadingShare === 'telegram'}
          />
          {!shareLinkOnly && (
            <Button
              title=""
              icon={faDownload}
              onClick={onClickPrint}
              className="btn btn-primary pr-actions-btn"
              disabled={loadingPrint}
            />
          )}
        </div>
      </div>

      {(loadingShare || loadingPrint || loading) && (
        <div className="pr-actions-loader">
          <LoaderMain systemProducts="Hotels" />
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(ProposalActions, 'ProposalActions');

